<template>
  <div>
    <slot name="title"></slot>

    <template v-if="!isPlainText">
      <!-- prettier-ignore -->
      <p id="personal-details" class="price-factor-statement">
        <template v-if="(isQA || isChangeCover) && thisData.name"> My name is {{ thisData.name }}. </template>
        <!-- Age -->
        I am <a href class="always-fresh" @click.prevent="jumpToField(1, 'DOB')">{{ thisData.age }} years old</a>
        <!-- State -->
        and I live in <a href class="always-fresh" @click.prevent="jumpToField(1, 'state')">{{ thisData.state }}</a>.
        <!-- Eligibility -->
        I can join Defence Health because <a href class="always-fresh" @click.prevent="jumpToField(1, 'eligibility')">{{ thisData.eligibility }}</a>. 
        <!-- Composition -->
        I want cover for <a href class="always-fresh" @click.prevent="jumpToField(2, 'coverComposition')">{{ thisData.compositionDisplay }}</a>.
        <!-- Partner -->
        <template v-if="hasPartner">
          <!-- partner's age-->
          My partner's age is <a href class="always-fresh" @click.prevent="jumpToField(2, 'partnerDOB')">{{ thisData.partnerAge }}</a>{{ thisData.composition === CoverComposition.COUPLE ? '.' : ' and' }}
        </template>
        <!-- number of children -->
        <template v-if="![CoverComposition.COUPLE, CoverComposition.SINGLE].includes(thisData.composition) && thisData.numberOfChildren > 0">
          I need cover for <a href class="always-fresh" @click.prevent="jumpToField(2, 'numberOfChildren')">{{ thisData.numberOfChildren }}</a>
          {{ thisData.numberOfChildren > 1 ? `children${isFamilyPlus ? '' : '.'}` : `child${isFamilyPlus ? '' : '.'}` }}
          <template v-if="isFamilyPlus">{{ thisData.numberOfChildren > 1 ? 'at least one of which' : 'who' }} 
          is aged between 21 to 30 inclusive, single and not studying full-time.</template>
        </template>
        <!-- cover type  -->
        I want <a href class="always-fresh" @click.prevent="jumpToField(3, 'coverType')">{{ thisData.coverType }}</a> for
        <!-- cover reason -->
        <a href class="always-fresh" @click.prevent="jumpToField(3, 'coverReason')">{{ thisData.coverReason }}</a>{{ needSpecificTreatments ? ':' : '.' }}
        <!-- IF COVER REASON IS SPECIFIC -->
        <template v-if="needSpecificTreatments">{{ generateSpecificTreatmentsList(thisData.hospitalNeeds, thisData.extrasNeeds) }}.</template>
      </p>

      <!-- prettier-ignore -->
      <p id="cover-deatils" class="price-factor-statement">
        <!-- Pregnancy -->
        <template v-if="(!isQA && (![CoverType.EXTRAS_ONLY].includes(thisData.coverType) && ![CoverReason.PLANNING_A_FAMILY].includes(thisData.coverReason)))">
          I <a href class="always-fresh" @click.prevent="jumpToField(3, 'noPregnancy')">do{{ thisData.isPregnancyCovered ? '' : ' not'}}</a>
          want pregnancy included in my quote.
        </template>
        <!-- Rebate tier -->
        <template v-if="thisData.rebateTier !== 'none' && !isEmptyString(thisData.rebateTier)">
          Price is calculated using my household income, which is 
          <a href class="always-fresh" @click.prevent="jumpToField(2, 'incomeTier')">{{ thisData.rebateTierDisplay }}</a>, meaning my Australian Government Rebate tier is
          <a href class="always-fresh" @click.prevent="jumpToField(2, 'incomeTier')">{{ computedIncomeTier(thisData.rebateTier) }}</a>.
        </template>
        
        <template v-else>
          No Australian Government <a href class="always-fresh" @click.prevent="jumpToField(2, 'incomeTier')">rebate</a> 
          is included in this premium.
        </template>
      </p>
    </template>

    <!-- Show plain text without links -->
    <template v-else>
      <!-- prettier-ignore -->
      <p id="partner-details" class="price-factor-statement">
        My name is {{ thisData.name }}. I am {{ thisData.age }} years old and I live in {{ thisData.state }}. I can join Defence Health because {{ thisData.eligibility }}. I want cover for
        {{ thisData.compositionDisplay }}.
        <!-- ONLY SHOW IF THERE IS A PARTNER -->
        <template v-if="hasPartner">
          My partner's age is {{ thisData.partnerAge }}{{ thisData.composition === CoverComposition.COUPLE ? '.' : ' and' }}
        </template>
        <!-- number of children -->
        <template v-if="![CoverComposition.COUPLE, CoverComposition.SINGLE].includes(thisData.composition) && thisData.numberOfChildren > 0">
          I need cover for {{ thisData.numberOfChildren }} {{ thisData.numberOfChildren > 1 ? `children${isFamilyPlus ? '' : '.'}` : `child${isFamilyPlus ? '' : '.'}` }}
          <template v-if="isFamilyPlus">{{ thisData.numberOfChildren > 1 ? 'at least one of which' : 'who' }} is aged between 21 to 30 inclusive, single and not studying full-time.</template>
        </template>
        <!-- cover type -->
        I want {{ thisData.coverType }} for {{ thisData.coverReason }}{{ needSpecificTreatments ? ':' : '.' }}
        <!-- IF COVER REASON IS SPECIFIC -->
        <template v-if="needSpecificTreatments">{{ generateSpecificTreatmentsList(thisData.hospitalNeeds, thisData.extrasNeeds) }}.</template>
      </p>

      <!-- prettier-ignore -->
      <p id="premium-info" class="price-factor-statement">
        <!-- pregnancy -->
        <template v-if="isQA || (!isQA && (![CoverType.EXTRAS_ONLY].includes(thisData.coverType) && ![CoverReason.PLANNING_A_FAMILY].includes(thisData.coverReason)))">
          I do {{ thisData.isPregnancyCovered ? '' : ' not' }}
          want pregnancy included in my quote.
        </template>
        <!-- rebate tier -->
        <template v-if="thisData.rebateTier !== 'none'">
          Price is calculated using my household income, which is {{ thisData.rebateTierDisplay }}, meaning my Australian Government Rebate tier is {{ this.summaryIncomeTier(thisData.rebateTier) }}.
        </template>
        <template v-else>
          No Australian Government rebate is included in this premium.
        </template>
        <!-- ONLY SHOW IF COMPOSITION IS NOT CHILDREN ONLY, and it's hidden for change cover page -->
        <template v-if="(thisData.composition !== CoverComposition.CHILDREN_ONLY) && !isChangeCover">
          I have{{ !thisData.hadHospitalInsurance ? ' not' : '' }} had hospital insurance for 10 years consecutively.
        </template>
      </p>
    </template>

    <div><slot name="button"></slot></div>
    <div id="price-factor-links"><slot name="link"></slot></div>
    <div><slot name="quoteValidityMessage"></slot></div>
  </div>
</template>

<script>
import { CoverComposition, CoverReason, CoverType } from '@general/js/enums';
import {
  calculateAge,
  summaryCoverComposition,
  summaryCoverType,
  summaryCoverReason,
  summaryRebate,
  summaryEligibility,
  generateSpecificTreatmentsList,
  summaryIncomeTier,
  isEmptyString,
} from '@general/js/utils';

export default {
  name: 'price-factor-statement',

  props: {
    data: {
      type: Object,
      required: true,
    },

    // It will display plain text without any links when it's true
    isPlainText: {
      type: Boolean,
    },

    // For change cover form, the sentence "I have not had hospital insurance for 10 years consecutively." will be hidden.
    isChangeCover: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      CoverComposition,
      CoverReason,
      CoverType,
    };
  },

  computed: {
    needSpecificTreatments() {
      return this.thisData.coverReason === this.CoverReason.SPECIFIC_TREATMENT;
    },

    isFamilyPlus() {
      return [this.CoverComposition.FAMILY_PLUS, this.CoverComposition.SINGLE_PARENT_PLUS].includes(this.thisData.composition);
    },

    hasPartner() {
      return [this.CoverComposition.COUPLE, this.CoverComposition.FAMILY, this.CoverComposition.FAMILY_PLUS].includes(this.thisData.composition);
    },

    isQA() {
      let isQuoteAcceptance = true;

      if (
        Object.keys(this.data).some((key) => {
          return key.includes('step');
        })
      ) {
        isQuoteAcceptance = false;
      }

      if (this.isPlainText === true) {
        return true;
      }

      return isQuoteAcceptance;
    },

    thisData() {
      const data = this.data;
      let isQA = this.isQA;

      if (this.isPlainText && isQA) {
        isQA = false;
      }

      let partnerAge;
      let hospitalNeeds;
      let extrasNeeds;

      if (isQA) {
        partnerAge = !isEmptyString(data.partnerAge) ? data.partnerAge : null;
        hospitalNeeds = data.hospitalNeeds;
        extrasNeeds = data.extrasNeeds;
      } else {
        // partner age
        if (data.step2.partnerDOB !== '') {
          partnerAge = calculateAge(data.step2.partnerDOB);
        } else {
          partnerAge = null;
        }

        // hospital needs
        if (data.step3.hospitalNeeds !== undefined && data.step3.hospitalNeeds.length > 0) {
          hospitalNeeds = data.step3.hospitalNeeds.map((obj) => {
            return obj.display;
          });
        } else {
          hospitalNeeds = null;
        }

        // extras needs
        if (data.step3.extrasNeeds !== undefined && data.step3.extrasNeeds.length > 0) {
          extrasNeeds = data.step3.extrasNeeds.map((obj) => {
            return obj.display;
          });
        } else {
          extrasNeeds = null;
        }
      }

      return {
        name: isQA ? data.name : data.step1.name,
        age: isQA ? data.age : calculateAge(data.step1.DOB),
        state: isQA ? data.addressState.toUpperCase() : data.step1.state.toUpperCase(),
        eligibility: isQA ? data.eligibility : summaryEligibility(data.step1.eligibility),
        composition: isQA ? data.composition : data.step2.coverComposition,
        compositionDisplay: isQA ? data.compositionDisplay : summaryCoverComposition(data.step2.coverComposition),
        partnerAge,
        numberOfChildren: isQA ? String(data.numberOfChildren) : String(data.step2.numberOfChildren),
        coverType: isQA ? data.coverType : summaryCoverType(data.step2?.coverType ?? data.step3?.coverType),
        coverReason: isQA ? summaryCoverReason(data.coverReason) : summaryCoverReason(data.step3.coverReason),
        hospitalNeeds,
        extrasNeeds,
        isPregnancyCovered: isQA ? data.isPregnancyCovered : !data.step3.noPregnancy,
        rebateTier: isQA ? data.rebateTier : data.step2.incomeTier,
        rebateTierDisplay: isQA ? data.rebateTierDisplay : summaryRebate(data.incomeTierOptions, data.step2.incomeTier),
        hadHospitalInsurance: isQA ? data.hadHospitalInsurance : data.step2.continuousCover,
      };
    },
  },

  methods: {
    jumpToField(step, field, clickedText = undefined) {
      this.$parent.jumpToField(step, field, clickedText);
    },

    computedIncomeTier(incomeTierData) {
      const array = incomeTierData.split('_').map((el) => {
        return el.charAt(0) + el.slice(1).toLowerCase();
      });
      return array.join(' ');
    },

    generateSpecificTreatmentsList,
    summaryIncomeTier,
    isEmptyString,
  },
};
</script>

<style lang="scss" scoped>
.price-factor-statement {
  span,
  a {
    display: inline !important;
  }
}
</style>
